import { Link, useParams, useSearchParams } from "react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { usePermissao } from "@/lib/use-permissao";
import { useFeature } from "@/lib/use-feature";
import { useSetPageHeader } from "@/lib/page-header";
import { EntityFilesPanel } from "@/components/files/EntityFilesPanel";
import { CabecalhoDoCadastro } from "@/components/partners/perfil/CabecalhoDoCadastro";
import { AbasDoCadastro, type AbaDoCadastro } from "@/components/partners/perfil/AbasDoCadastro";
import { VisaoGeralDoCadastro } from "@/components/partners/perfil/VisaoGeralDoCadastro";
import { DadosDoCadastro } from "@/components/partners/perfil/DadosDoCadastro";
import { ExtratoDoCadastroPainel } from "@/components/partners/perfil/ExtratoDoCadastroPainel";
import { PedidosDoCadastro } from "@/components/partners/perfil/PedidosDoCadastro";
import { PrecosDoCadastro } from "@/components/partners/perfil/PrecosDoCadastro";

const CLIENTES = new Set(["DENTIST", "CLINIC_NETWORK"]);

/**
 * O perfil do cadastrado: quem é e, se for cliente conectado do laboratório, o movimento dele —
 * pedidos, extrato e preços — num lugar só. Fornecedor, radiologia e outro laboratório ficam com
 * Dados e Arquivos.
 */
export function CadastroPerfil() {
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const consulta = trpc.partner.getById.useQuery({ id });
  const c = consulta.data;
  const ehLaboratorio = (useAuth().user?.activeOrganization?.types ?? []).includes("DENTAL_LAB");
  const vePreco = usePermissao("prices.view");
  // Extrato é do plano pago (Financeiro); sem ele, a aba nem aparece.
  const temFinanceiro = useFeature("financeiro");
  const veFinanceiro = usePermissao("finance.view");
  const veExtrato = temFinanceiro && veFinanceiro;
  useSetPageHeader({ title: c?.name ?? "Cadastro", subtitle: "Cadastros" }, [c?.name]);

  if (consulta.isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
      </div>
    );
  }
  if (!c) return <p className="py-24 text-center text-sm text-zinc-500">{consulta.error?.message ?? "Cadastro não encontrado."}</p>;

  const cliente = ehLaboratorio && CLIENTES.has(c.type);
  const conectado = !!c.linkedOrganizationId;
  const abas: AbaDoCadastro[] = [
    ...(cliente && conectado ? (["visao", "pedidos"] as const) : []),
    ...(cliente && conectado && veExtrato ? (["extrato"] as const) : []),
    ...(cliente && conectado && vePreco ? (["precos"] as const) : []),
    "dados",
    "arquivos",
  ];
  const pedida = params.get("aba") as AbaDoCadastro | null;
  const aba = pedida && abas.includes(pedida) ? pedida : abas[0]!;
  const mudarAba = (a: AbaDoCadastro) =>
    setParams(
      (p) => {
        p.set("aba", a);
        return p;
      },
      { replace: true },
    );

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      <Link to="/lab/cadastros" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground">
        <ArrowLeft className="h-4 w-4" />
        Cadastros
      </Link>
      <CabecalhoDoCadastro cadastro={c as any} />
      {cliente && !conectado && (
        <p className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-[12px] text-zinc-600 dark:border-border dark:bg-muted/30 dark:text-muted-foreground">
          Pedidos, extrato e preços aparecem quando este cliente se conectar — os pedidos chegam pela conta dele na plataforma.
        </p>
      )}
      <AbasDoCadastro abas={abas} ativa={aba} onAba={mudarAba} />
      {aba === "visao" && <VisaoGeralDoCadastro id={id} />}
      {aba === "pedidos" && <PedidosDoCadastro clientOrganizationId={c.linkedOrganizationId!} />}
      {aba === "extrato" && <ExtratoDoCadastroPainel partnerId={id} />}
      {aba === "precos" && <PrecosDoCadastro cadastro={c as any} />}
      {aba === "dados" && <DadosDoCadastro id={id} />}
      {aba === "arquivos" && <EntityFilesPanel relatedType="PARTNER" relatedId={id} />}
    </div>
  );
}
